import { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { X } from "lucide-react";

/**
 * Freeform "Custom Features" list — shared by both the PF2e Full Sheet and the D&D 5e sheet
 * so players can log any feat, feature, or trait that isn't in our seeded catalog, regardless
 * of which book they got it from.
 */
export function CustomFeaturesSection({ character, patchChar }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "" });
  const features = character.custom_features || [];

  return (
    <>
      <p className="text-xs text-[#f1e0c5]/40 -mt-1">
        Not every feat/feature is in our seeded catalog yet — add anything from your book (or imported via a paper-sheet photo) here so you never miss a rule you're entitled to.
      </p>
      <div className="space-y-2">
        {features.map((cf, idx) => (
          <div key={idx} data-testid={`custom-feature-${idx}`} className="flex items-start gap-2 bg-black/20 rounded-lg px-3 py-2">
            <div className="flex-1">
              <p className="text-sm">{cf.name}</p>
              {cf.description && <p className="text-xs text-[#f1e0c5]/40 font-body">{cf.description}</p>}
            </div>
            <button
              data-testid={`custom-feature-remove-${idx}`}
              onClick={() => patchChar({ custom_features: features.filter((_, i) => i !== idx) })}
              className="text-[#f1e0c5]/30 hover:text-[#D90429] shrink-0"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      {adding ? (
        <div className="space-y-2 bg-black/20 rounded-lg p-3">
          <Input data-testid="custom-feature-name-input" placeholder="Feature name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="bg-black/30 border-[#4a3424]/50 h-8 text-xs" />
          <Textarea data-testid="custom-feature-description-input" placeholder="What does it do? (optional)" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="bg-black/30 border-[#4a3424]/50 text-xs min-h-16" />
          <div className="flex gap-2">
            <Button
              data-testid="custom-feature-save-button"
              size="sm"
              disabled={!draft.name.trim()}
              onClick={() => {
                patchChar({ custom_features: [...features, { name: draft.name.trim(), description: draft.description.trim() }] });
                setDraft({ name: "", description: "" });
                setAdding(false);
              }}
              className="rounded-full bg-[#4d7c0f] hover:bg-[#3f6608]"
            >
              Add
            </Button>
            <button data-testid="custom-feature-cancel-button" onClick={() => setAdding(false)} className="text-xs text-[#f1e0c5]/40 hover:text-white px-2">Cancel</button>
          </div>
        </div>
      ) : (
        <Button data-testid="custom-feature-add-button" variant="secondary" onClick={() => setAdding(true)} className="w-full rounded-full">
          + Add Custom Feature
        </Button>
      )}
    </>
  );
}
